import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AxiosError } from "axios";

import {
  runModule,
  type DossierResponse,
  type DossierSection,
} from "@/api/objekte";
import { Markdown } from "@/components/Markdown";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface Props {
  slug: string;
  sections: DossierSection[];
}

const RERUNNABLE_SECTION_IDS = new Set(["00-stammdaten", "01-listing"]);

export function DossierTabs({ slug, sections }: Props) {
  const [activeId, setActiveId] = useState<string>(
    sections[0]?.id ?? "",
  );

  if (sections.length === 0) {
    return (
      <div
        className="rounded-lg border p-10 text-center"
        data-testid="dossier-empty"
      >
        <p className="text-sm font-medium">
          Keine Markdown-Sektionen für dieses Objekt.
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          Im Repo unter <code className="text-xs">objekte/&lt;slug&gt;/*.md</code>{" "}
          fehlen die kanonischen Dossier-Files. <code>git pull</code> + Coolify-
          Reindex sollten das Bild korrigieren.
        </p>
      </div>
    );
  }

  const active = sections.find((s) => s.id === activeId) ?? sections[0];
  const canRerun = RERUNNABLE_SECTION_IDS.has(active.id);

  const qc = useQueryClient();
  const rerun = useMutation({
    mutationFn: () => runModule(slug, "dossier"),
    onSuccess: (data) => {
      // Patch the cached dossier in place: replace any sections the API
      // returned with their fresh content. The container can't write back
      // to git-tracked files, so we don't refetch — we just merge locally.
      qc.setQueryData<DossierResponse | undefined>(
        ["dossier", slug],
        (old) =>
          old
            ? {
                ...old,
                sections: old.sections.map((s) =>
                  data.sections[s.id]
                    ? { ...s, markdown: data.sections[s.id] }
                    : s,
                ),
              }
            : old,
      );
      toast.success("Dossier neu gerendert", {
        description:
          "Im Container ist das Repo read-only. Lokal kopieren + commit + push.",
      });
    },
    onError: (err) => {
      const detail =
        err instanceof AxiosError
          ? (err.response?.data as { detail?: string } | undefined)?.detail ??
            err.message
          : err instanceof Error
            ? err.message
            : "Unbekannter Fehler";
      toast.error("Re-run fehlgeschlagen", { description: detail });
    },
  });

  return (
    <div className="rounded-lg border" data-testid="dossier-tabs">
      <nav
        className="flex flex-wrap gap-1 border-b bg-muted/20 p-2"
        role="tablist"
        aria-label="Dossier-Sektionen"
      >
        {sections.map((s) => (
          <button
            key={s.id}
            type="button"
            role="tab"
            aria-selected={s.id === active.id}
            data-testid={`tab-${s.id}`}
            onClick={() => setActiveId(s.id)}
            className={cn(
              "rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
              s.id === active.id
                ? "bg-background shadow-sm"
                : "text-muted-foreground hover:bg-background/50",
            )}
          >
            <span className="mr-1.5 font-mono text-muted-foreground">
              {String(s.nr).padStart(2, "0")}
            </span>
            {s.title}
          </button>
        ))}
      </nav>
      <div
        className="p-6"
        role="tabpanel"
        aria-labelledby={`tab-${active.id}`}
        data-testid={`tabpanel-${active.id}`}
      >
        <Markdown markdown={active.markdown} />

        {canRerun && (
          <div className="mt-6 flex items-center gap-3 border-t pt-4">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => rerun.mutate()}
              disabled={rerun.isPending}
              data-testid="rerun-dossier"
            >
              {rerun.isPending ? "Rendere …" : "Aus listing.json neu rendern"}
            </Button>
            <p className="text-xs text-muted-foreground">
              Container ist read-only — Output lokal kopieren + commit + push,
              um den Stand persistent zu machen.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
